// Authentication configuration for multiple providers
export interface AuthConfig {
  provider: AuthProvider;
  keycloak?: KeycloakConfig;
  okta?: OktaConfig;
  azureAd?: AzureAdConfig;
  google?: GoogleConfig;
  github?: GitHubConfig;
}

export type AuthProvider = 'none' | 'keycloak' | 'okta' | 'azure-ad' | 'google' | 'github';

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
  redirectUri: string;
}

export interface OktaConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
}

export interface AzureAdConfig {
  tenantId: string;
  clientId: string;
  redirectUri: string;
}

export interface GoogleConfig {
  clientId: string;
  redirectUri: string;
}

export interface GitHubConfig {
  clientId: string;
  redirectUri: string;
}

// Helper function to safely access environment variables in Vite
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  try {
    // For Vite, environment variables are available in import.meta.env
    // Note: Only VITE_ prefixed variables are exposed by default
    // But REACT_APP_ variables are also supported for compatibility
    if (import.meta?.env?.[key]) {
      const envValue = import.meta.env[key];
      return String(envValue);
    }
    
    return defaultValue;
  } catch (error) {
    logger.warn(`Error accessing environment variable ${key}:`, error);
    return defaultValue;
  }
};

// Safe window location origin access
const getOrigin = (): string => {
  try {
    return window.location.origin;
  } catch (error) {
    return 'http://localhost:5173'; // Vite default port
  }
};

// Default configuration - works without any environment variables
export const defaultAuthConfig: AuthConfig = {
  // Default to 'none' if no environment variable is set
  provider: (getEnvVar('REACT_APP_AUTH_PROVIDER') as AuthProvider) || 
           (getEnvVar('VITE_AUTH_PROVIDER') as AuthProvider) || 
           'none',
  
  keycloak: {
    url: getEnvVar('REACT_APP_KEYCLOAK_URL') || getEnvVar('VITE_KEYCLOAK_URL') || 'http://localhost:8180',
    realm: getEnvVar('REACT_APP_KEYCLOAK_REALM') || getEnvVar('VITE_KEYCLOAK_REALM') || 'hop',
    clientId: getEnvVar('REACT_APP_KEYCLOAK_CLIENT_ID') || getEnvVar('VITE_KEYCLOAK_CLIENT_ID') || 'hop-studio-frontend',
    redirectUri: getEnvVar('REACT_APP_KEYCLOAK_REDIRECT_URI') || getEnvVar('VITE_KEYCLOAK_REDIRECT_URI') || `${getOrigin()}/auth/callback`,
  },
  
  okta: {
    domain: getEnvVar('REACT_APP_OKTA_DOMAIN') || getEnvVar('VITE_OKTA_DOMAIN'),
    clientId: getEnvVar('REACT_APP_OKTA_CLIENT_ID') || getEnvVar('VITE_OKTA_CLIENT_ID'),
    redirectUri: getEnvVar('REACT_APP_OKTA_REDIRECT_URI') || getEnvVar('VITE_OKTA_REDIRECT_URI') || `${getOrigin()}/auth/callback`,
  },
  
  azureAd: {
    tenantId: getEnvVar('REACT_APP_AZURE_TENANT_ID') || getEnvVar('VITE_AZURE_TENANT_ID'),
    clientId: getEnvVar('REACT_APP_AZURE_CLIENT_ID') || getEnvVar('VITE_AZURE_CLIENT_ID'),
    redirectUri: getEnvVar('REACT_APP_AZURE_REDIRECT_URI') || getEnvVar('VITE_AZURE_REDIRECT_URI') || `${getOrigin()}/auth/callback`,
  },
  
  google: {
    clientId: getEnvVar('REACT_APP_GOOGLE_CLIENT_ID') || getEnvVar('VITE_GOOGLE_CLIENT_ID'),
    redirectUri: getEnvVar('REACT_APP_GOOGLE_REDIRECT_URI') || getEnvVar('VITE_GOOGLE_REDIRECT_URI') || `${getOrigin()}/auth/callback`,
  },
  
  github: {
    clientId: getEnvVar('REACT_APP_GITHUB_CLIENT_ID') || getEnvVar('VITE_GITHUB_CLIENT_ID'),
    redirectUri: getEnvVar('REACT_APP_GITHUB_REDIRECT_URI') || getEnvVar('VITE_GITHUB_REDIRECT_URI') || `${getOrigin()}/auth/callback`,
  },
};

// Helper to determine if authentication is enabled
export const isAuthEnabled = (): boolean => {
  return defaultAuthConfig.provider !== 'none';
};

// Helper to get the current provider config
export const getCurrentProviderConfig = () => {
  const { provider } = defaultAuthConfig;
  switch (provider) {
    case 'keycloak':
      return defaultAuthConfig.keycloak;
    case 'okta':
      return defaultAuthConfig.okta;
    case 'azure-ad':
      return defaultAuthConfig.azureAd;
    case 'google':
      return defaultAuthConfig.google;
    case 'github':
      return defaultAuthConfig.github;
    default:
      return null;
  }
}; 